import React from "react";
import { useTranslations } from "@danishagro/shared/src/contexts/translations/translations.context";
import { DA_Title } from "@danishagro/shared/src/components/atoms/Title/Title.component";
import { formatNumber } from "@danishagro/shared/src/helpers/formatNumber.helper";
import { useAppData } from "@danishagro/shared/src/contexts/appData.context";
import { useScreen } from "@danishagro/shared/src/contexts/screen.context";
import { cn } from "@danishagro/shared/src/helpers/classNames.helper";
import S from "./OrderDetailsSubtotal.module.scss";

interface OrderDetailsSubtotalProps {
    totalPriceWithoutVat: number;
    totalPriceWithVat: number;
    shipping: number;
    vat: number;
    handlingFee: number;
}

export const OrderDetailsSubtotal = ({
    totalPriceWithoutVat,
    totalPriceWithVat,
    shipping,
    vat,
    handlingFee,
}: OrderDetailsSubtotalProps) => {
    const { getDictionaryString } = useTranslations();
    const { currentCulture, siteVariant } = useAppData();
    const { isMobileOrTablet } = useScreen();

    const subtotalFormatted = formatNumber(totalPriceWithoutVat, currentCulture, { decimals: 2 });

    return (
        <div className={S.tableWrapper}>
            <table className={S.table}>
                {siteVariant.alias === "hatting-agro-b2b" ? (
                    <div className={S.shippingNewContainer}>
                        <thead className={S.head}>
                            <tr>
                                <th>
                                    <DA_Title h5>
                                        {getDictionaryString("profile.orderHistory.subtotal")}:
                                    </DA_Title>
                                </th>
                                <th className={S.noWrap}>{subtotalFormatted}</th>
                            </tr>

                            <tr>
                                <th colSpan={2}>
                                    <div className={S.separator} />
                                </th>
                            </tr>
                        </thead>

                        {/* Shipping */}
                        <tbody className={S.body}>
                            <tr>
                                <td className={S.shippingNew}>
                                    <span>
                                        {getDictionaryString("profile.orderHistory.handlingFee")}:
                                    </span>
                                </td>
                                <td className={S.noWrap}>
                                    {formatNumber(handlingFee, currentCulture, { decimals: 2 })}
                                </td>
                            </tr>
                            <tr>
                                <td className={S.shippingNew}>
                                    <span>
                                        {getDictionaryString("profile.orderHistory.shipping")}:
                                    </span>
                                </td>
                                <td className={S.noWrap}>
                                    {formatNumber(shipping, currentCulture, { decimals: 2 })}
                                </td>
                            </tr>

                            <tr>
                                <td colSpan={2}>
                                    <div className={cn(S.separator, S.separatorMt)} />
                                </td>
                            </tr>

                            {/* Total price without vat */}
                            <tr className={S.totalPriceWithoutVat}>
                                <td>
                                    {getDictionaryString(
                                        "profile.orderHistory.totalPriceWithoutVat"
                                    )}
                                    :
                                </td>
                                <td className={S.noWrap}>
                                    {formatNumber(totalPriceWithoutVat, currentCulture, {
                                        decimals: 2,
                                    })}
                                </td>
                            </tr>

                            {/* Vat */}
                            <tr>
                                <td>
                                    {isMobileOrTablet
                                        ? getDictionaryString("profile.orderHistory.vatMobile")
                                        : getDictionaryString("profile.orderHistory.vat")}
                                    :
                                </td>
                                <td className={S.noWrap}>
                                    {formatNumber(vat, currentCulture, { decimals: 2 })}
                                </td>
                            </tr>

                            <tr>
                                <td colSpan={2}>
                                    <div className={cn(S.separator, S.separatorBig)} />
                                </td>
                            </tr>

                            {/* Total price with vat */}
                            <tr className={S.totalPriceWithVat}>
                                <td>
                                    {getDictionaryString("profile.orderHistory.totalPriceWithVat")}:
                                </td>
                                <td className={S.noWrap}>
                                    {formatNumber(totalPriceWithVat, currentCulture, {
                                        decimals: 2,
                                    })}
                                </td>
                            </tr>

                            <tr>
                                <td colSpan={2}>
                                    <div className={cn(S.separator, S.separatorBig)} />
                                </td>
                            </tr>
                        </tbody>
                    </div>
                ) : (
                    <>
                        <thead className={S.head}>
                            <tr>
                                <th>
                                    <DA_Title h5>
                                        {getDictionaryString("profile.orderHistory.subtotal")}:
                                    </DA_Title>
                                </th>
                                <th className={S.noWrap}>{subtotalFormatted}</th>
                            </tr>

                            <tr>
                                <th colSpan={2}>
                                    <div className={S.separator} />
                                </th>
                            </tr>
                        </thead>

                        {/* Shipping */}
                        <tbody className={S.body}>
                            <tr>
                                <td className={S.shipping}>
                                    <span>
                                        {getDictionaryString("profile.orderHistory.shipment")}:
                                    </span>
                                    <span>
                                        {getDictionaryString(
                                            "profile.orderHistory.shipment.details"
                                        )}
                                    </span>
                                </td>
                                <td className={S.noWrap}>
                                    {formatNumber(shipping, currentCulture, { decimals: 2 })}
                                </td>
                            </tr>

                            <tr>
                                <td colSpan={2}>
                                    <div className={cn(S.separator, S.separatorMt)} />
                                </td>
                            </tr>

                            {/* Total price without vat */}
                            <tr className={S.totalPriceWithoutVat}>
                                <td>
                                    {getDictionaryString(
                                        "profile.orderHistory.totalPriceWithoutVat"
                                    )}
                                    :
                                </td>
                                <td className={S.noWrap}>
                                    {formatNumber(totalPriceWithoutVat, currentCulture, {
                                        decimals: 2,
                                    })}
                                </td>
                            </tr>

                            {/* Vat */}
                            <tr>
                                <td>
                                    {isMobileOrTablet
                                        ? getDictionaryString("profile.orderHistory.vatMobile")
                                        : getDictionaryString("profile.orderHistory.vat")}
                                    :
                                </td>
                                <td className={S.noWrap}>
                                    {formatNumber(vat, currentCulture, { decimals: 2 })}
                                </td>
                            </tr>

                            <tr>
                                <td colSpan={2}>
                                    <div className={S.separator} />
                                </td>
                            </tr>

                            {/* Total price with vat */}
                            <tr className={S.totalPriceWithVat}>
                                <td>
                                    {getDictionaryString("profile.orderHistory.totalPriceWithVat")}:
                                </td>
                                <td className={S.noWrap}>
                                    {formatNumber(totalPriceWithVat, currentCulture, {
                                        decimals: 2,
                                    })}
                                </td>
                            </tr>

                            <tr>
                                <td colSpan={2}>
                                    <div className={S.separator} />
                                </td>
                            </tr>
                        </tbody>
                    </>
                )}
            </table>
        </div>
    );
};
